import {
  DashboardState,
  EApiContentType,
  EApiHttpMethod,
  EApiResponseContentType,
  ESdChangeReason,
  IActionDefinitionDto,
  IAuthorizationDto
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { ActionPicker } from "../properties-panel/action-picker";
import { AuthorizationPanel } from "../properties-panel/authorization-panel/authorization-panel";
import { ScreenInputList } from "../properties-panel/screen-input-list";
import "./api-management-properties.scss";

/**
 * The three request/response pickers, as data. They were antd `Select`s with inline
 * `Select.Option` children, which is why they stayed white on the dark theme — antd's own
 * dropdown chrome is not themed by the designer's tokens. `KuikaSelectbox` is.
 */
const HTTP_METHOD_OPTIONS: KuikaSelectboxOption[] = [
  { value: EApiHttpMethod.Post, label: "POST", testId: "apiHttpMethodOption-POST" },
  { value: EApiHttpMethod.Get, label: "GET", testId: "apiHttpMethodOption-GET" },
  { value: EApiHttpMethod.Put, label: "PUT", testId: "apiHttpMethodOption-PUT" },
  { value: EApiHttpMethod.Delete, label: "DELETE", testId: "apiHttpMethodOption-DELETE" },
  { value: EApiHttpMethod.Patch, label: "PATCH", testId: "apiHttpMethodOption-PATCH" }
];

const REQUEST_CONTENT_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: EApiContentType.ApplicationJson, label: "application/json" },
  { value: EApiContentType.FormUrlEncoded, label: "application/x-www-form-urlencoded" },
  { value: EApiContentType.ApplicationXml, label: "application/xml" }
];

const RESPONSE_CONTENT_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: EApiResponseContentType.ApplicationJson, label: "application/json" },
  { value: EApiResponseContentType.TextHtml, label: "text/html" }
];

/** What `rc-collapse` hands the `expandIcon` renderer; only the open flag is read here. */
interface ICollapseExpandIconProps {
  isActive?: boolean;
}

export interface IApiManagementPropertiesState {
  isSubmitButtonLoading: boolean;
  isAuthorizationMenuVisible: boolean;
}

export interface IApiManagementPropertiesProps {}

class ApiManagementProperties extends PureSdComponent<
  IApiManagementPropertiesProps & IWithSDProps & IWithContextProp,
  IApiManagementPropertiesState
> {
  constructor(props: IApiManagementPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isSubmitButtonLoading: false,
      isAuthorizationMenuVisible: false
    };
  }

  getActions = (): IActionDefinitionDto[] => {
    let actionList: IActionDefinitionDto[] = [];
    actionList = _.cloneDeep(this.props.sd.actionDefinitions);
    return actionList;
  };

  handleVisibleAuthorizationDropdown = (visible: boolean) => {
    this.setState({ isAuthorizationMenuVisible: visible });
  };

  onClickCopyAuthorizationToClipboard = async () => {
    const apiMethod = this.props.sd.getSelectedApiMethod();

    const authorization: IAuthorizationDto | undefined = apiMethod?.authorization;

    if (authorization) {
      const authorizationJson: string = JSON.stringify(authorization, null, 0);
      this.props.sd.setScreenAuthorizationClipboard(authorizationJson);
      KMainFunctions.displaySuccessNotification("Authorization copied to clipboard");
    }
    this.setState({ isAuthorizationMenuVisible: false });
  };

  onClickPasteAuthorizationFromClipboard = async () => {
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();

    if (!api || !apiMethod) {
      return;
    }

    const authorizationJson: string | null = this.props.sd.screenAuthorizationClipboard;
    if (authorizationJson) {
      const authorization: IAuthorizationDto = JSON.parse(authorizationJson);
      apiMethod.authorization = authorization;

      this.props.sd
        .saveApiManagements(api)
        .catch((e) => {
          console.error(e);
        })
        .then(() => {
          KMainFunctions.displaySuccessNotification("Authorization pasted from clipboard");
        })
        .finally(() => {
          this.setState({ isAuthorizationMenuVisible: false });
        });
    }
  };

  /**
   * The chevron the whole right-hand column uses: it points right when closed and is rotated a
   * quarter turn when open. antd positioned it absolutely, which is why it used to carry a
   * `right: 12` — the kit lays the header out as a row and places it with `iconPlacement`.
   */
  renderExpandIcon = (panelProps: ICollapseExpandIconProps): ReactNode => (
    <KuikaSvg
      name="chevron-right"
      type="arrows"
      width={16}
      height={16}
      className={panelProps.isActive ? "kuika_rotate90deg" : "kuika_rotate0deg"}
    />
  );

  onHttpMethodChange = (httpMethod: EApiHttpMethod) => {
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();

    if (!api || !apiMethod) {
      return;
    }

    apiMethod.httpMethod = httpMethod;

    this.props.sd
      .saveApiManagements(api)
      .then(() => {
        KMainFunctions.displaySuccessNotification("HTTP method updated");
      })
      .catch((e) => {
        console.error(e);
      });
  };

  onContentTypeChange = (contentType: EApiContentType) => {
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();

    if (!api || !apiMethod) {
      return;
    }

    apiMethod.contentType = contentType;

    this.props.sd
      .saveApiManagements(api)
      .then(() => {
        KMainFunctions.displaySuccessNotification("Content type updated");
      })
      .catch((e) => {
        console.error(e);
      });
  };

  onResponseContentTypeChange = (responseContentType: EApiResponseContentType) => {
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();

    if (!api || !apiMethod) {
      return;
    }

    apiMethod.responseContentType = responseContentType;

    this.props.sd
      .saveApiManagements(api)
      .then(() => {
        KMainFunctions.displaySuccessNotification("Response content type updated");
      })
      .catch((e) => {
        console.error(e);
      });
  };

  isHttpMethodWithBody = (): boolean => {
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (!apiMethod) return true;
    const method = apiMethod.httpMethod ?? EApiHttpMethod.Post;
    return method !== EApiHttpMethod.Get && method !== EApiHttpMethod.Delete;
  };

  render() {
    const api = this.props.sd.getSelectedApi();
    const selectedApiMethod = this.props.sd.getSelectedApiMethod();

    return (
      <>
        {api && (
          <div>
            <div
              style={{
                fontSize: "11px",
                padding: "5px"
              }}
              className="row kuika_topPanel btn-group-vertical"
            >
              {this.props.sd.dashboardState === DashboardState.apiManagement && selectedApiMethod && (
                <>
                  <div style={{ margin: "12px 6px" }}>
                    <KuikaCollapsePanel
                      iconPlacement="right"
                      className="kuika-right-panel__collapse kuika-right-panel__collapse--api-management kuika-api-management__collapse"
                      expandIcon={this.renderExpandIcon}
                      items={[
                        {
                          key: "pnlAuthorization",
                          label: (
                            <div className="kuika-api-management__header">
                              <KuikaTextMedium
                                className="kuika-api-management__header-label"
                                data-testid="authorizationText"
                              >
                                Authorization
                              </KuikaTextMedium>
                              <div
                                className="kuika-api-management__header-menu"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <KuikaDropdownMenu
                                  options={[
                                    {
                                      key: "1",
                                      label: "Copy Authorization",
                                      onClick: () => {
                                        this.onClickCopyAuthorizationToClipboard();
                                      }
                                    },
                                    ...(this.props.sd.screenAuthorizationClipboard
                                      ? [
                                          {
                                            key: "2",
                                            label: "Paste Authorization",
                                            onClick: () => {
                                              this.onClickPasteAuthorizationFromClipboard();
                                            }
                                          }
                                        ]
                                      : [])
                                  ]}
                                  open={this.state.isAuthorizationMenuVisible}
                                  setOpen={this.handleVisibleAuthorizationDropdown}
                                  trigger="click"
                                >
                                  <KuikaSvg name="dots-vertical" type="styling-panel-icons" width={16} height={16} />
                                </KuikaDropdownMenu>
                              </div>
                            </div>
                          ),
                          children: <AuthorizationPanel />
                        }
                      ]}
                    />
                  </div>
                  <div style={{ margin: "12px 6px" }}>
                    <span className="kuika-typography-text--medium">Request</span>
                    <KuikaCollapsePanel
                      iconPlacement="right"
                      className="kuika-right-panel__collapse kuika-right-panel__collapse--request kuika-api-management__collapse kuika-api-management__collapse--spaced"
                      expandIcon={this.renderExpandIcon}
                      items={[
                        {
                          key: "pnlHttpMethod",
                          label: (
                            <KuikaTextMedium
                              className="kuika-api-management__header-label"
                              data-testid="apiHttpMethodText"
                            >
                              HTTP Method
                            </KuikaTextMedium>
                          ),
                          children: (
                            <div className="kuika-api-management__field">
                              <KuikaSelectbox
                                size="md"
                                value={selectedApiMethod?.httpMethod ?? EApiHttpMethod.Post}
                                data-testid="apiHttpMethodSelect"
                                options={HTTP_METHOD_OPTIONS}
                                onChange={(value: number) => this.onHttpMethodChange(value)}
                              />
                            </div>
                          )
                        },
                        // An entry the list simply does not carry when the verb has no body, which is
                        // what the conditional `Collapse.Panel` amounted to.
                        ...(this.isHttpMethodWithBody()
                          ? [
                              {
                                key: "pnlContentType",
                                label: (
                                  <KuikaTextMedium className="kuika-api-management__header-label">
                                    Content Type
                                  </KuikaTextMedium>
                                ),
                                children: (
                                  <div className="kuika-api-management__field">
                                    <KuikaSelectbox
                                      size="md"
                                      value={selectedApiMethod?.contentType ?? EApiContentType.ApplicationJson}
                                      options={REQUEST_CONTENT_TYPE_OPTIONS}
                                      onChange={(value: number) => this.onContentTypeChange(value)}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : [])
                      ]}
                    />
                  </div>
                  <div style={{ margin: "12px 6px" }}>
                    <span className="kuika-typography-text--medium">Response</span>
                    <KuikaCollapsePanel
                      iconPlacement="right"
                      className="kuika-right-panel__collapse kuika-right-panel__collapse--response kuika-api-management__collapse kuika-api-management__collapse--spaced"
                      expandIcon={this.renderExpandIcon}
                      items={[
                        {
                          key: "pnlResponseContentType",
                          label: (
                            <KuikaTextMedium className="kuika-api-management__header-label">
                              Content Type
                            </KuikaTextMedium>
                          ),
                          children: (
                            <div className="kuika-api-management__field">
                              <KuikaSelectbox
                                size="md"
                                value={
                                  selectedApiMethod?.responseContentType ?? EApiResponseContentType.ApplicationJson
                                }
                                options={RESPONSE_CONTENT_TYPE_OPTIONS}
                                onChange={(value: number) => this.onResponseContentTypeChange(value)}
                              />
                            </div>
                          )
                        }
                      ]}
                    />
                  </div>
                  <ActionPicker />
                  <ScreenInputList />
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.UpdateAuth) {
      return true;
    }
    return false;
  };
}

const apiManagementProperties = withContext<IApiManagementPropertiesProps>(
  withSD<IApiManagementPropertiesProps>(ApiManagementProperties)
);
export { apiManagementProperties as ApiManagementProperties };
